// Bulk-generate plant photos for plants without image_url.
// Job-based: starts background task, polls status, shows live progress.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Camera, X, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const PARTS = [
  { key: "ganze", label: "Ganze Pflanze" },
  { key: "blatt", label: "Blatt" },
  { key: "bluete", label: "Blüte" },
  { key: "frucht", label: "Frucht / Samen" },
  { key: "standort", label: "Standort / Habitat" },
  { key: "detail", label: "Detail / Makro" },
];

export default function BulkPhotoFill({ onDone }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null);
  const [part, setPart] = useState("ganze");
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/plants/without-image/count").then((r) => setCount(r.data?.count ?? 0)).catch(() => setCount(0));
  }, [open]);

  // Poll job status
  useEffect(() => {
    if (!job?.id || job?.status !== "running") {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    const tick = async () => {
      try {
        const r = await api.get(`/admin/plants/bulk-images/${job.id}`);
        setJob(r.data);
        if (r.data?.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (r.data?.status === "completed") {
            toast.success(`Fertig: ${r.data.succeeded} generiert, ${r.data.failed} fehlgeschlagen`);
            if (onDone) onDone();
          } else if (r.data?.status === "error") {
            toast.error("Job abgebrochen: " + (r.data.error || "Unbekannter Fehler"));
          }
        }
      } catch (err) {
        // Soft-fail; keep polling
      }
    };
    pollRef.current = setInterval(tick, 1500);
    tick();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [job?.id, job?.status, onDone]);

  const start = async () => {
    setStarting(true);
    try {
      const r = await api.post("/admin/plants/bulk-images", { part, concurrency: 3 });
      setJob({ id: r.data.job_id, status: "running", total: r.data.total, done: 0, succeeded: 0, failed: 0, items: [], current_plant: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Starten");
    } finally {
      setStarting(false);
    }
  };

  const close = () => {
    setOpen(false);
    if (job?.status !== "running") {
      // Reset state when modal closed after completion
      setJob(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-secondary !py-2 !px-3 text-sm"
        title="Bulk-Fotos für Pflanzen ohne Bild generieren"
        data-testid="bulk-photo-fill-btn"
      >
        <Camera className="w-4 h-4" /> Fotos (echt / KI)
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ backgroundColor: "rgba(26,47,28,0.55)" }}
          onClick={(job?.status === "running") ? undefined : close}
          data-testid="bulk-photo-fill-modal"
        >
          <div
            className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b sticky top-0 bg-white" style={{ borderColor: "var(--wp-border)" }}>
              <div className="flex items-start justify-between gap-3 mb-1">
                <div>
                  <div className="overline">Bulk-Foto-Fill</div>
                  <h3 className="font-display text-2xl" style={{ color: "var(--wp-olive)" }}>Echte Pflanzen-Fotos</h3>
                </div>
                <button onClick={close} className="opacity-60 hover:opacity-100 p-1" data-testid="bulk-photo-close" disabled={job?.status === "running"}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              {job?.status === "running" && (
                <div className="text-xs opacity-70 mt-2">Schließen erst möglich wenn fertig. Du kannst aber zur Seite wechseln – läuft im Hintergrund weiter.</div>
              )}
            </div>

            <div className="p-5 sm:p-6">
              <RealPhotoSearch part={part} setPart={setPart} />
              <div className="overline mt-6 mb-2 opacity-70">KI-Bilder – nur als letzte Option</div>
              {!job ? (
                <>
                  <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: "var(--wp-cream)" }}>
                    {count === null ? "Lade …" : count === 0
                      ? <span>Alle Pflanzen haben bereits ein Bild ✨</span>
                      : <span><strong>{count}</strong> Pflanze{count === 1 ? "" : "n"} ohne Bild gefunden. Werden mit Gemini Nano Banana erstellt.</span>}
                  </div>
                  {count > 0 && (
                    <>
                      <label className="block mb-4">
                        <div className="overline mb-1">Welcher Pflanzenteil?</div>
                        <select
                          value={part}
                          onChange={(e) => setPart(e.target.value)}
                          className="w-full px-3 py-2.5 rounded-lg border text-sm"
                          style={{ borderColor: "var(--wp-border)" }}
                          data-testid="bulk-photo-part"
                        >
                          {PARTS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                        </select>
                      </label>
                      <div className="text-xs opacity-60 mb-4">
                        ⚠️ Dauert ca. <strong>{Math.round((count * 8) / 3 / 60)} Min</strong> ({count} × ~8s ÷ 3 parallel). Verbraucht LLM-Budget.
                      </div>
                      <button
                        onClick={start}
                        disabled={starting || count === 0}
                        className="btn-primary w-full justify-center !py-3"
                        data-testid="bulk-photo-start"
                      >
                        {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                        {starting ? "Starte …" : `KI-Generierung starten (${count} Bilder)`}
                      </button>
                    </>
                  )}
                </>
              ) : (
                <BulkProgress job={job} />
              )}
            </div>

            {job && job.status !== "running" && (
              <div className="p-4 border-t flex gap-2" style={{ borderColor: "var(--wp-border)" }}>
                <button onClick={close} className="btn-primary flex-1 justify-center" data-testid="bulk-photo-finish">
                  Fertig
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function RealPhotoSearch({ part, setPart }) {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  const start = async () => {
    try {
      const { data } = await api.post("/admin/plants/enrich-async", { mode: "photos", part, plant_ids: [] });
      setJob({ status: "running", progress: 0, message: "startet …" });
      timer.current = setInterval(async () => {
        try {
          const r = await api.get(`/admin/jobs/${data.job_id}`);
          setJob(r.data);
          if (r.data.status !== "running" && r.data.status !== "queued") clearInterval(timer.current);
        } catch { /* keep polling */ }
      }, 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Start fehlgeschlagen"); }
  };
  const changed = (job?.result?.plants || []).filter((p) => p.changes?.length);
  return (
    <div className="space-y-3" data-testid="real-photo-search">
      <label className="block">
        <div className="overline mb-1">Pflanzenteil wählen</div>
        <select value={part} onChange={(e) => setPart(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="real-photo-part">
          {PARTS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </label>
      <p className="text-xs opacity-70">Sucht für alle Pflanzen, denen dieser Teil noch fehlt, ein echtes Foto auf Wikimedia Commons (mit Fotograf & Lizenz). „Ganze Pflanze“ ersetzt KI-Hauptbilder.</p>
      <button onClick={start} disabled={job?.status === "running"} className="btn-primary w-full justify-center !py-3" data-testid="real-photo-start">
        {job?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Echte Fotos suchen
      </button>
      {job && (
        <div className="text-xs space-y-1" data-testid="real-photo-status">
          <div>{job.message} {job.status === "running" ? `(${job.progress || 0} %)` : ""}</div>
          {job.status === "done" && <div>{changed.length} Pflanze(n) mit neuem Foto: {changed.map((p) => p.name).join(", ") || "–"}</div>}
        </div>
      )}
    </div>
  );
}

function BulkProgress({ job }) {
  const pct = job.total > 0 ? Math.round((job.done / job.total) * 100) : 0;
  const items = job.items || [];

  return (
    <div data-testid="bulk-photo-progress">
      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-2">
        <div className="text-sm font-medium">{job.done} / {job.total}</div>
        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--wp-cream)" }}>
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: "var(--wp-olive)" }}
          />
        </div>
        <div className="text-sm font-medium tabular-nums">{pct}%</div>
      </div>
      <div className="flex items-center gap-4 text-xs opacity-70 mb-4">
        <span><CheckCircle2 className="w-3 h-3 inline" style={{ color: "var(--wp-primary)" }} /> {job.succeeded} ok</span>
        <span><XCircle className="w-3 h-3 inline" style={{ color: "var(--wp-toxic)" }} /> {job.failed} fehlgeschlagen</span>
        {job.status === "running" && job.current_plant && (
          <span className="ml-auto italic">⏳ {job.current_plant}</span>
        )}
        {job.status === "completed" && <span className="ml-auto" style={{ color: "var(--wp-primary)" }}>✓ Fertig</span>}
        {job.status === "error" && <span className="ml-auto" style={{ color: "var(--wp-toxic)" }}>⚠ Fehler</span>}
      </div>

      {/* Items list */}
      <div className="space-y-1 max-h-[40vh] overflow-y-auto pr-1">
        {items.length === 0 && job.status === "running" && (
          <div className="text-xs opacity-60 italic py-4 text-center">Generiere erste Bilder …</div>
        )}
        {items.slice().reverse().map((it, idx) => (
          <div
            key={`${it.plant_id}-${idx}`}
            className="flex items-center gap-2 text-xs p-2 rounded"
            style={{ backgroundColor: idx === 0 ? "var(--wp-cream)" : "transparent" }}
            data-testid={`bulk-photo-item-${it.status}`}
          >
            {it.status === "ok" ? (
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--wp-primary)" }} />
            ) : (
              <XCircle className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--wp-toxic)" }} />
            )}
            <span className="font-medium truncate flex-1">{it.name_de}</span>
            {it.status === "failed" && it.error && (
              <span className="opacity-60 text-[10px] truncate max-w-[40%]" title={it.error}>{it.error}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
